import React from "react";
import { ApolloError } from "@apollo/client";

import WellnessPlanErrorPage, {
  type IWellnessPlanErrorPageCta,
} from "./WellnessPlanErrorPage";
import {
  getWellnessPlanErrorConfig,
  getWellnessPlanLocalError,
  parseWellnessPlanError,
  type WellnessPlanErrorCtaKey,
  type WellnessPlanLocalErrorKind,
} from "./wellnessPlanError.helpers";

type CtaResolver = (
  key: WellnessPlanErrorCtaKey,
) => IWellnessPlanErrorPageCta | undefined;

interface IWellnessPlanErrorViewProps {
  /** Maps a CTA key to a concrete CTA (see useWellnessPlanErrorCta). */
  resolveCta: CtaResolver;
  /** Client-side error situation. Takes precedence over apolloError. */
  localKind?: WellnessPlanLocalErrorKind;
  /** Backend error to parse into title/description + CTA. */
  apolloError?: ApolloError | null;
}

/**
 * Single entry point for rendering the unified wellness error screen. Resolves
 * the title/description/image/CTA from either a local error kind or a backend
 * Apollo error so containers don't have to assemble props inline.
 *
 * @param {IWellnessPlanErrorViewProps} props - Error source and CTA resolver.
 * @return {JSX.Element} The unified error screen.
 */
const WellnessPlanErrorView = ({
  resolveCta,
  localKind,
  apolloError,
}: IWellnessPlanErrorViewProps): JSX.Element => {
  if (localKind) {
    const content = getWellnessPlanLocalError(localKind);
    return (
      <WellnessPlanErrorPage
        imageSrc={content.imageSrc}
        title={content.title}
        description={content.description}
        primaryCta={resolveCta(content.ctaKey)}
      />
    );
  }

  const parsed = parseWellnessPlanError(apolloError);
  const config = getWellnessPlanErrorConfig(parsed.code);

  return (
    <WellnessPlanErrorPage
      imageSrc={config.imageSrc}
      title={parsed.title}
      description={parsed.description}
      primaryCta={resolveCta(config.ctaKey)}
    />
  );
};

export default WellnessPlanErrorView;
